import _ from 'lodash';

import { isSuperuser } from 'js/lib/user';

import { type NounValues, type VerbValues, Verbs } from 'bundles/user-permissions/src/utils/constants';
import type {
  GetInstanceIdsFunc,
  GetNounsFunc,
  GetVerbsFunc,
  GrantedFunc,
  InstanceIdFunc,
  NounFunc,
  PermissionWithFunc,
  PermissionsParsed,
  VerbFunc,
} from 'bundles/user-permissions/src/utils/types';

/**
 *
 *  Return callable in that functions in the format of:
 *
 *  BOOLEAN examples for existence checks:
 *      permissionWith().noun('noun').granted(): boolean
 *      permissionWith().noun('noun').verb('verb').granted(): boolean
 *      permissionWith().noun('noun').verb('verb').instanceId('id').granted(): boolean
 *
 *  ARRAY values based on keys provided:
 *      permissionWith().getNouns(): Array<NounValues>
 *      permissionWith().noun('noun').getVerbs(): Array<VerbValues>
 *      permissionWith().noun('noun').verb('verb').getInstanceIds(): Array<string | number>
 *
 */
export const permissionsBuilder = (permissions: PermissionsParsed = {}): PermissionWithFunc => {
  let _noun: string | null | undefined = null;
  let _verb: string | null | undefined = null;
  let _instanceId: string | number | null | undefined = null;
  const granted: GrantedFunc = () => {
    // if user is superuser, override restrict permissions
    if (isSuperuser() && _verb === Verbs.RESTRICT) {
      return false;
    }
    /* using just the uniqueness of the instanceId */
    const key = _.compact([_noun, _verb, _instanceId?.toString()]);
    return isSuperuser() ? true : !!_.get(permissions, key);
  };
  const getInstanceIds: GetInstanceIdsFunc = () => {
    const key = _.compact([_noun, _verb]);
    const instances = _.get(permissions, key, null);
    return instances ? Object.keys(instances) : [];
  };
  const getVerbs: GetVerbsFunc = () => {
    const key = _.compact([_noun]);
    const verbs = _.get(permissions, key, null);
    return verbs ? Object.keys(verbs) : [];
  };
  const getNouns: GetNounsFunc = () => {
    return permissions ? Object.keys(permissions) : [];
  };

  const instanceId: InstanceIdFunc = (value) => {
    _instanceId = value;
    return value ? { granted } : { granted: () => false };
  };

  const verb: VerbFunc = (value: VerbValues) => {
    _verb = value;
    return value
      ? {
          instanceId,
          getInstanceIds,
          granted,
        }
      : {
          instanceId,
          getInstanceIds,
          granted: () => false,
        };
  };

  const noun: NounFunc = (value: NounValues) => {
    _noun = value;
    return value
      ? {
          verb,
          getVerbs,
          granted,
        }
      : {
          verb,
          getVerbs,
          granted: () => false,
        };
  };

  return () => {
    _noun = null;
    _verb = null;
    _instanceId = null;
    return {
      noun,
      getNouns,
    };
  };
};
